import React, { useState, useEffect, useCallback } from 'react';
import { api } from '@/api';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import {
  ArrowLeft, AlertTriangle, FileText, Layers, Clock,
  Plus, CheckCircle2, Scale, Shield, Calendar,
  Gavel, BookOpen, Handshake, Activity, Lock, Brain
} from 'lucide-react';

import EvidenceLocker from './EvidenceLocker';
import ChronologyBuilder from './ChronologyBuilder';
import SubmissionsWorkspace from './SubmissionsWorkspace';
import SettlementWorkspace from './SettlementWorkspace';
import DecisionsCompliance from './DecisionsCompliance';
import AIAnalysisPanel from './AIAnalysisPanel';

const STATUS_COLORS = {
  potential_dispute: 'bg-amber-100 text-amber-700', internal_escalation: 'bg-orange-100 text-orange-700',
  negotiation_active: 'bg-blue-100 text-blue-700', formal_dispute: 'bg-red-100 text-red-700',
  referral_in_preparation: 'bg-purple-100 text-purple-700', forum_active: 'bg-indigo-100 text-indigo-700',
  awaiting_decision: 'bg-cyan-100 text-cyan-700', decision_issued: 'bg-teal-100 text-teal-700',
  compliance_pending: 'bg-amber-100 text-amber-700', settled: 'bg-green-100 text-green-700',
  closed: 'bg-slate-100 text-slate-600',
};
const ISSUE_STATUS_COLORS = {
  draft: 'bg-slate-100 text-slate-600', live: 'bg-blue-100 text-blue-700',
  partially_agreed: 'bg-amber-100 text-amber-700', fully_agreed: 'bg-green-100 text-green-700',
  referred: 'bg-purple-100 text-purple-700', decided: 'bg-teal-100 text-teal-700',
  settled: 'bg-green-100 text-green-700', closed: 'bg-slate-100 text-slate-600',
};
const NOTICE_STATUS_COLORS = {
  draft: 'bg-slate-100 text-slate-600', ready_to_issue: 'bg-blue-100 text-blue-700',
  issued: 'bg-purple-100 text-purple-700', served: 'bg-green-100 text-green-700',
  response_due: 'bg-amber-100 text-amber-700', responded: 'bg-teal-100 text-teal-700',
  time_bar_risk: 'bg-red-100 text-red-700',
};
const STEP_STATUS_COLORS = {
  not_started: 'bg-slate-100 text-slate-600', pending: 'bg-amber-100 text-amber-700',
  in_progress: 'bg-blue-100 text-blue-700', completed: 'bg-green-100 text-green-700',
  waived: 'bg-slate-100 text-slate-500', missed: 'bg-red-100 text-red-700',
  overridden: 'bg-purple-100 text-purple-700',
};
const RISK_COLORS = { low: 'bg-green-100 text-green-700', medium: 'bg-amber-100 text-amber-700', high: 'bg-red-100 text-red-700', critical: 'bg-red-600 text-white' };

const RISK_LABELS = {
  no_owner_assigned: 'No owner assigned', deadline_missed: 'Deadline missed',
  time_bar_risk: 'Time-bar risk', no_issues_framed: 'No issues framed',
  evidence_incomplete: 'Evidence incomplete', chronology_incomplete: 'Chronology incomplete',
};

export default function DisputeWorkspace({ projectId, matterId, settings, onBack }) {
  const [matter, setMatter] = useState(null);
  const [issues, setIssues] = useState([]);
  const [notices, setNotices] = useState([]);
  const [steps, setSteps] = useState([]);
  const [activity, setActivity] = useState([]);
  const [evidence, setEvidence] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showAddIssue, setShowAddIssue] = useState(false);
  const [showAddNotice, setShowAddNotice] = useState(false);
  const [issueForm, setIssueForm] = useState({ issue_title: '', issue_type: 'general', clause_references_text: '', claimant_position: '', respondent_position: '', relief_sought: '', amount_component: '', time_component_days: '', priority_score: 5 });
  const [noticeForm, setNoticeForm] = useState({ notice_type: '', contract_clause: '', required_by_date: '', served_on_company: '', notes: '' });

  const loadData = useCallback(async () => {
    try {
      const [mRes, iRes, nRes, sRes, aRes, eRes] = await Promise.all([
        api.disputes.getMatter(matterId),
        api.disputes.listIssues(matterId),
        api.disputes.listNotices(matterId),
        api.disputes.listSteps(matterId),
        api.disputes.getActivity(matterId),
        api.disputes.listEvidence(matterId),
      ]);
      setMatter(mRes.data);
      setIssues(iRes.data || []);
      setNotices(nRes.data || []);
      setSteps(sRes.data || []);
      setActivity(aRes.data || []);
      setEvidence(eRes.data || []);
    } catch { toast.error('Failed to load matter'); } finally { setLoading(false); }
  }, [matterId]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleAddIssue = async () => {
    if (!issueForm.issue_title.trim()) { toast.error('Issue title required'); return; }
    try {
      await api.disputes.createIssue(matterId, { ...issueForm, matter_id: matterId, amount_component: issueForm.amount_component ? parseFloat(issueForm.amount_component) : null, time_component_days: issueForm.time_component_days ? parseInt(issueForm.time_component_days) : null });
      toast.success('Issue created');
      setShowAddIssue(false);
      setIssueForm({ issue_title: '', issue_type: 'general', clause_references_text: '', claimant_position: '', respondent_position: '', relief_sought: '', amount_component: '', time_component_days: '', priority_score: 5 });
      loadData();
    } catch { toast.error('Failed to create issue'); }
  };

  const handleAddNotice = async () => {
    if (!noticeForm.notice_type) { toast.error('Notice type required'); return; }
    try {
      await api.disputes.createNotice(matterId, { ...noticeForm, matter_id: matterId });
      toast.success('Notice created');
      setShowAddNotice(false);
      setNoticeForm({ notice_type: '', contract_clause: '', required_by_date: '', served_on_company: '', notes: '' });
      loadData();
    } catch { toast.error('Failed to create notice'); }
  };

  const handleStepUpdate = async (stepId, status) => {
    try { await api.disputes.updateStep(stepId, { status }); toast.success('Step updated'); loadData(); } catch { toast.error('Update failed'); }
  };

  const handleNoticeUpdate = async (noticeId, status) => {
    try {
      const updates = { status };
      if (status === 'issued') updates.issued_date = new Date().toISOString().split('T')[0];
      if (status === 'served') updates.served_date = new Date().toISOString().split('T')[0];
      await api.disputes.updateNotice(noticeId, updates);
      toast.success('Notice updated');
      loadData();
    } catch { toast.error('Update failed'); }
  };

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin h-8 w-8 border-2 border-slate-300 border-t-slate-800 rounded-full" /></div>;
  if (!matter) return null;

  const profile = matter.profile || {};
  const riskFlags = matter.risk_flags || [];

  return (
    <div className="space-y-4" data-testid="dispute-workspace">
      {/* Top Banner */}
      <div className="bg-white rounded-lg border p-4">
        <div className="flex items-center gap-2 mb-2">
          <Button variant="ghost" size="sm" onClick={onBack} className="gap-1 -ml-2"><ArrowLeft className="h-3.5 w-3.5" /> Register</Button>
        </div>
        <div className="flex flex-col lg:flex-row lg:items-center gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="font-mono text-xs text-slate-400">{matter.matter_ref}</span>
              <Badge className={`text-[10px] ${STATUS_COLORS[matter.status]}`}>{matter.status?.replace(/_/g, ' ')}</Badge>
              <Badge className={`text-[10px] ${RISK_COLORS[matter.risk_rating]}`}>{matter.risk_rating}</Badge>
              {matter.privileged_flag && <Badge className="bg-slate-800 text-white text-[9px] gap-0.5"><Shield className="h-2.5 w-2.5" /> Privileged</Badge>}
            </div>
            <h1 className="text-lg font-bold text-slate-900">{matter.title}</h1>
            <div className="flex items-center gap-4 mt-1 text-xs text-slate-500">
              <span>{matter.contract_family}</span>
              {matter.counterparty && <span>vs {matter.counterparty}</span>}
              {matter.amount_in_dispute > 0 && <span className="font-mono font-medium">{new Intl.NumberFormat('en-ZA', { style: 'currency', currency: matter.currency || 'ZAR', maximumFractionDigits: 0 }).format(matter.amount_in_dispute)}</span>}
              {matter.time_in_dispute_days > 0 && <span>{matter.time_in_dispute_days} days</span>}
              {matter.next_deadline_date && <span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> {matter.next_deadline_date}</span>}
            </div>
          </div>
        </div>
      </div>

      <div className="flex gap-4">
        {/* Main Content */}
        <div className="flex-1 min-w-0">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="bg-white border mb-4 flex-wrap h-auto gap-0.5 p-1">
              <TabsTrigger value="overview" className="text-xs gap-1"><Scale className="h-3.5 w-3.5" /> Overview</TabsTrigger>
              <TabsTrigger value="issues" className="text-xs gap-1"><Layers className="h-3.5 w-3.5" /> Issues ({issues.length})</TabsTrigger>
              <TabsTrigger value="notices" className="text-xs gap-1"><FileText className="h-3.5 w-3.5" /> Notices ({notices.length})</TabsTrigger>
              <TabsTrigger value="procedure" className="text-xs gap-1"><Gavel className="h-3.5 w-3.5" /> Procedure</TabsTrigger>
              <TabsTrigger value="evidence" className="text-xs gap-1"><Lock className="h-3.5 w-3.5" /> Evidence</TabsTrigger>
              <TabsTrigger value="chronology" className="text-xs gap-1"><Clock className="h-3.5 w-3.5" /> Chronology</TabsTrigger>
              <TabsTrigger value="submissions" className="text-xs gap-1"><BookOpen className="h-3.5 w-3.5" /> Submissions</TabsTrigger>
              <TabsTrigger value="settlement" className="text-xs gap-1"><Handshake className="h-3.5 w-3.5" /> Settlement</TabsTrigger>
              <TabsTrigger value="decisions" className="text-xs gap-1"><CheckCircle2 className="h-3.5 w-3.5" /> Decisions</TabsTrigger>
              <TabsTrigger value="ai" className="text-xs gap-1"><Brain className="h-3.5 w-3.5" /> AI</TabsTrigger>
              <TabsTrigger value="activity" className="text-xs gap-1"><Activity className="h-3.5 w-3.5" /> Activity</TabsTrigger>
            </TabsList>

            {/* OVERVIEW TAB */}
            <TabsContent value="overview" className="space-y-4">
              <Card><CardContent className="p-4">
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Summary</h3>
                <p className="text-sm text-slate-700">{matter.summary || 'No summary provided.'}</p>
                {matter.clause_references && <p className="text-xs text-slate-500 mt-2">Clauses: {matter.clause_references}</p>}
              </CardContent></Card>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <Button variant="outline" size="sm" className="text-xs gap-1 h-9" onClick={() => { setActiveTab('notices'); setShowAddNotice(true); }}><FileText className="h-3 w-3" /> Create Notice</Button>
                <Button variant="outline" size="sm" className="text-xs gap-1 h-9" onClick={() => { setActiveTab('issues'); setShowAddIssue(true); }}><Layers className="h-3 w-3" /> Add Issue</Button>
                <Button variant="outline" size="sm" className="text-xs gap-1 h-9" onClick={() => setActiveTab('chronology')}><Clock className="h-3 w-3" /> Build Chronology</Button>
                <Button variant="outline" size="sm" className="text-xs gap-1 h-9" onClick={() => setActiveTab('settlement')}><Handshake className="h-3 w-3" /> Settlement Offer</Button>
              </div>
              <Card><CardContent className="p-4">
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Contract Pathway ({matter.contract_family})</h3>
                <div className="flex items-center gap-1 overflow-x-auto pb-2">
                  {(profile.stages || []).map((s, i) => {
                    const isActive = matter.forum_stage === s.code;
                    const isPast = (profile.stages || []).findIndex(x => x.code === matter.forum_stage) > i;
                    return (
                      <React.Fragment key={s.code}>
                        {i > 0 && <div className={`w-6 h-0.5 flex-shrink-0 ${isPast ? 'bg-green-400' : 'bg-slate-200'}`} />}
                        <div className={`flex-shrink-0 px-2.5 py-1.5 rounded text-[10px] font-medium border transition-colors ${isActive ? 'bg-slate-900 text-white border-slate-900' : isPast ? 'bg-green-50 text-green-700 border-green-200' : 'bg-white text-slate-500 border-slate-200'}`}>
                          {s.label}
                        </div>
                      </React.Fragment>
                    );
                  })}
                </div>
              </CardContent></Card>
              {issues.length > 0 && (
                <Card><CardContent className="p-4">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Top Issues</h3>
                  {issues.slice(0, 5).map(iss => (
                    <div key={iss.id} className="flex items-center gap-2 py-1.5 border-b last:border-b-0">
                      <span className="font-mono text-[10px] text-slate-400">{iss.issue_ref}</span>
                      <span className="text-sm flex-1">{iss.issue_title}</span>
                      <Badge className={`text-[10px] ${ISSUE_STATUS_COLORS[iss.issue_status]}`}>{iss.issue_status?.replace(/_/g, ' ')}</Badge>
                    </div>
                  ))}
                </CardContent></Card>
              )}
            </TabsContent>

            {/* ISSUES TAB */}
            <TabsContent value="issues" className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-bold text-slate-700">Issues ({issues.length})</h3>
                <Button size="sm" className="bg-slate-900 hover:bg-slate-800 gap-1 text-xs" onClick={() => setShowAddIssue(true)} data-testid="add-issue-btn"><Plus className="h-3 w-3" /> Add Issue</Button>
              </div>
              {issues.length === 0 ? (
                <Card><CardContent className="p-8 text-center"><Layers className="h-10 w-10 text-slate-300 mx-auto mb-2" /><p className="text-sm text-slate-500">No issues framed yet</p></CardContent></Card>
              ) : issues.map(iss => (
                <Card key={iss.id} data-testid={`issue-${iss.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-mono text-xs text-slate-400">{iss.issue_ref}</span>
                          <Badge className={`text-[10px] ${ISSUE_STATUS_COLORS[iss.issue_status]}`}>{iss.issue_status?.replace(/_/g, ' ')}</Badge>
                          <Badge variant="outline" className="text-[10px]">{iss.issue_type?.replace(/_/g, ' ')}</Badge>
                        </div>
                        <h4 className="text-sm font-semibold text-slate-800">{iss.issue_title}</h4>
                        {iss.clause_references_text && <p className="text-xs text-slate-500 mt-0.5">Clauses: {iss.clause_references_text}</p>}
                        <div className="grid grid-cols-2 gap-3 mt-2">
                          {iss.claimant_position && <div><p className="text-[10px] font-bold text-slate-500 uppercase">Claimant Position</p><p className="text-xs text-slate-700 mt-0.5">{iss.claimant_position}</p></div>}
                          {iss.respondent_position && <div><p className="text-[10px] font-bold text-slate-500 uppercase">Respondent Position</p><p className="text-xs text-slate-700 mt-0.5">{iss.respondent_position}</p></div>}
                        </div>
                      </div>
                      <div className="text-right shrink-0 space-y-1">
                        {iss.amount_component > 0 && <p className="text-xs font-mono font-medium">{new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR', maximumFractionDigits: 0 }).format(iss.amount_component)}</p>}
                        {iss.time_component_days > 0 && <p className="text-xs text-slate-500">{iss.time_component_days} days</p>}
                        <p className="text-[10px] text-slate-400">Priority: {iss.priority_score}/10</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </TabsContent>

            {/* NOTICES TAB */}
            <TabsContent value="notices" className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-bold text-slate-700">Notices ({notices.length})</h3>
                <Button size="sm" className="bg-slate-900 hover:bg-slate-800 gap-1 text-xs" onClick={() => setShowAddNotice(true)} data-testid="add-notice-btn"><Plus className="h-3 w-3" /> Create Notice</Button>
              </div>
              {notices.length === 0 ? (
                <Card><CardContent className="p-8 text-center"><FileText className="h-10 w-10 text-slate-300 mx-auto mb-2" /><p className="text-sm text-slate-500">No notices created yet</p></CardContent></Card>
              ) : notices.map(n => (
                <Card key={n.id} data-testid={`notice-${n.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-mono text-xs text-slate-400">{n.notice_ref}</span>
                          <Badge className={`text-[10px] ${NOTICE_STATUS_COLORS[n.status]}`}>{n.status?.replace(/_/g, ' ')}</Badge>
                        </div>
                        <h4 className="text-sm font-medium text-slate-800 capitalize">{n.notice_type?.replace(/_/g, ' ')}</h4>
                        <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                          {n.contract_clause && <span>Clause: {n.contract_clause}</span>}
                          {n.served_on_company && <span>To: {n.served_on_company}</span>}
                          {n.required_by_date && <span>Due: {n.required_by_date}</span>}
                        </div>
                      </div>
                      <div className="flex gap-1 shrink-0">
                        {n.status === 'draft' && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => handleNoticeUpdate(n.id, 'ready_to_issue')}>Mark Ready</Button>}
                        {n.status === 'ready_to_issue' && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => handleNoticeUpdate(n.id, 'issued')}>Issue</Button>}
                        {n.status === 'issued' && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => handleNoticeUpdate(n.id, 'served')}>Mark Served</Button>}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </TabsContent>

            {/* PROCEDURE TAB */}
            <TabsContent value="procedure" className="space-y-3">
              <h3 className="text-sm font-bold text-slate-700">Procedure & Timetable ({steps.length} steps)</h3>
              {steps.length === 0 ? (
                <Card><CardContent className="p-8 text-center"><Gavel className="h-10 w-10 text-slate-300 mx-auto mb-2" /><p className="text-sm text-slate-500">No procedural steps</p></CardContent></Card>
              ) : (
                <div className="space-y-1.5">
                  {steps.map((s) => (
                    <div key={s.id} className="flex items-center gap-3 p-3 bg-white rounded-lg border" data-testid={`step-${s.id}`}>
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${s.status === 'completed' ? 'bg-green-100 text-green-700' : s.status === 'missed' ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-500'}`}>
                        {s.status === 'completed' ? <CheckCircle2 className="h-4 w-4" /> : s.sequence_no}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-800">{s.step_label}</p>
                        <div className="flex items-center gap-3 text-xs text-slate-500">
                          {s.due_date && <span className={s.due_date < new Date().toISOString().split('T')[0] && s.status !== 'completed' ? 'text-red-500 font-medium' : ''}>{s.due_date}</span>}
                          {s.mandatory_flag && <Badge variant="outline" className="text-[8px]">Mandatory</Badge>}
                          {s.auto_generated_flag && <span className="text-slate-300">Auto</span>}
                        </div>
                      </div>
                      <Badge className={`text-[10px] ${STEP_STATUS_COLORS[s.status]}`}>{s.status?.replace(/_/g, ' ')}</Badge>
                      {s.status !== 'completed' && s.status !== 'waived' && (
                        <Select value="" onValueChange={v => handleStepUpdate(s.id, v)}>
                          <SelectTrigger className="h-7 w-[110px] text-xs"><SelectValue placeholder="Update" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="in_progress">In Progress</SelectItem>
                            <SelectItem value="completed">Completed</SelectItem>
                            <SelectItem value="waived">Waived</SelectItem>
                            <SelectItem value="missed">Missed</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* EVIDENCE TAB */}
            <TabsContent value="evidence">
              <EvidenceLocker matterId={matterId} issues={issues} />
            </TabsContent>

            {/* CHRONOLOGY TAB */}
            <TabsContent value="chronology">
              <ChronologyBuilder matterId={matterId} issues={issues} evidence={evidence} />
            </TabsContent>

            {/* SUBMISSIONS TAB */}
            <TabsContent value="submissions">
              <SubmissionsWorkspace matterId={matterId} issues={issues} evidence={evidence} />
            </TabsContent>

            {/* SETTLEMENT TAB */}
            <TabsContent value="settlement">
              <SettlementWorkspace matterId={matterId} />
            </TabsContent>

            {/* DECISIONS TAB */}
            <TabsContent value="decisions">
              <DecisionsCompliance matterId={matterId} profile={profile} />
            </TabsContent>

            {/* AI TAB */}
            <TabsContent value="ai">
              <AIAnalysisPanel matterId={matterId} />
            </TabsContent>

            {/* ACTIVITY TAB */}
            <TabsContent value="activity" className="space-y-2">
              <h3 className="text-sm font-bold text-slate-700">Activity Log</h3>
              {activity.length === 0 ? (
                <Card><CardContent className="p-8 text-center"><Activity className="h-10 w-10 text-slate-300 mx-auto mb-2" /><p className="text-sm text-slate-500">No activity yet</p></CardContent></Card>
              ) : activity.map(a => (
                <div key={a.id} className="flex items-start gap-3 p-2.5 bg-white rounded border">
                  <div className="w-1.5 h-1.5 rounded-full bg-slate-400 mt-1.5 shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm text-slate-700">{a.description}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{a.created_by} | {new Date(a.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                  </div>
                </div>
              ))}
            </TabsContent>
          </Tabs>
        </div>

        {/* Right Risk Panel */}
        <div className="w-56 shrink-0 hidden lg:block">
          <Card className={riskFlags.length > 0 ? 'border-red-200' : ''}>
            <CardContent className="p-3">
              <h3 className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1">
                <AlertTriangle className="h-3 w-3" /> Why At Risk
              </h3>
              {riskFlags.length === 0 ? (
                <p className="text-xs text-green-600">No active risks</p>
              ) : (
                <div className="space-y-1.5">
                  {riskFlags.map((r, i) => (
                    <div key={i} className="flex items-start gap-1.5 p-1.5 bg-red-50 rounded text-xs text-red-700">
                      <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
                      <span>{RISK_LABELS[r] || r.replace(/_/g, ' ')}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Add Issue Dialog */}
      <Dialog open={showAddIssue} onOpenChange={setShowAddIssue}>
        <DialogContent className="sm:max-w-lg">
          <DialogTitle className="font-barlow font-bold uppercase">Add Issue</DialogTitle>
          <div className="space-y-3 mt-2 max-h-[55vh] overflow-y-auto pr-1">
            <div><Label className="text-xs">Issue Title *</Label><Input value={issueForm.issue_title} onChange={e => setIssueForm(f => ({...f, issue_title: e.target.value}))} placeholder="e.g. Whether contractor is entitled to EOT for Event 7" data-testid="issue-title-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Issue Type</Label>
                <Select value={issueForm.issue_type} onValueChange={v => setIssueForm(f => ({...f, issue_type: v}))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">General</SelectItem><SelectItem value="payment">Payment</SelectItem><SelectItem value="eot_delay">EOT / Delay</SelectItem>
                    <SelectItem value="variation">Variation</SelectItem><SelectItem value="certificate">Certificate</SelectItem><SelectItem value="defects">Defects</SelectItem>
                    <SelectItem value="valuation">Valuation</SelectItem><SelectItem value="final_account">Final Account</SelectItem><SelectItem value="termination">Termination</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Clause References</Label><Input value={issueForm.clause_references_text} onChange={e => setIssueForm(f => ({...f, clause_references_text: e.target.value}))} placeholder="e.g. 63.1, 63.3" /></div>
            </div>
            <div><Label className="text-xs">Claimant Position</Label><Textarea value={issueForm.claimant_position} onChange={e => setIssueForm(f => ({...f, claimant_position: e.target.value}))} rows={2} placeholder="Our position on this issue..." /></div>
            <div><Label className="text-xs">Respondent Position</Label><Textarea value={issueForm.respondent_position} onChange={e => setIssueForm(f => ({...f, respondent_position: e.target.value}))} rows={2} placeholder="Their position on this issue..." /></div>
            <div><Label className="text-xs">Relief Sought</Label><Input value={issueForm.relief_sought} onChange={e => setIssueForm(f => ({...f, relief_sought: e.target.value}))} placeholder="What outcome is sought" /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs">Amount (ZAR)</Label><Input type="number" value={issueForm.amount_component} onChange={e => setIssueForm(f => ({...f, amount_component: e.target.value}))} /></div>
              <div><Label className="text-xs">Time (days)</Label><Input type="number" value={issueForm.time_component_days} onChange={e => setIssueForm(f => ({...f, time_component_days: e.target.value}))} /></div>
              <div><Label className="text-xs">Priority (1-10)</Label><Input type="number" min={1} max={10} value={issueForm.priority_score} onChange={e => setIssueForm(f => ({...f, priority_score: parseInt(e.target.value) || 5}))} /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddIssue(false)}>Cancel</Button>
            <Button className="bg-slate-900 hover:bg-slate-800" onClick={handleAddIssue} data-testid="submit-issue"><Plus className="h-4 w-4 mr-1" /> Add Issue</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Notice Dialog */}
      <Dialog open={showAddNotice} onOpenChange={setShowAddNotice}>
        <DialogContent className="sm:max-w-md">
          <DialogTitle className="font-barlow font-bold uppercase">Create Notice</DialogTitle>
          <div className="space-y-3 mt-2">
            <div><Label className="text-xs">Notice Type *</Label>
              <Select value={noticeForm.notice_type} onValueChange={v => setNoticeForm(f => ({...f, notice_type: v}))}>
                <SelectTrigger data-testid="notice-type-select"><SelectValue placeholder="Select type..." /></SelectTrigger>
                <SelectContent>
                  {(profile.notice_types || []).map(nt => <SelectItem key={nt} value={nt}>{nt.replace(/_/g, ' ')}</SelectItem>)}
                  <SelectItem value="reservation_of_rights">Reservation of Rights</SelectItem>
                  <SelectItem value="compliance_demand_after_decision">Compliance Demand</SelectItem>
                  <SelectItem value="settlement_offer_wp">Settlement Offer (WP)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Contract Clause</Label><Input value={noticeForm.contract_clause} onChange={e => setNoticeForm(f => ({...f, contract_clause: e.target.value}))} placeholder="e.g. W1.3(1)" /></div>
            <div><Label className="text-xs">Required By Date</Label><Input type="date" value={noticeForm.required_by_date} onChange={e => setNoticeForm(f => ({...f, required_by_date: e.target.value}))} /></div>
            <div><Label className="text-xs">Serve On (Company)</Label><Input value={noticeForm.served_on_company} onChange={e => setNoticeForm(f => ({...f, served_on_company: e.target.value}))} placeholder="Counterparty company" /></div>
            <div><Label className="text-xs">Notes</Label><Textarea value={noticeForm.notes} onChange={e => setNoticeForm(f => ({...f, notes: e.target.value}))} rows={2} placeholder="Internal notes..." /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddNotice(false)}>Cancel</Button>
            <Button className="bg-slate-900 hover:bg-slate-800" onClick={handleAddNotice} data-testid="submit-notice"><Plus className="h-4 w-4 mr-1" /> Create Notice</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
